import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { WeatherV4Response } from '@moment/net/accuweather-types.ts';
import Weather from '@moment/components/Weather/Weather.tsx';
import { useBridge } from '@moment/bridge/bridge-provider.tsx';
import { BridgeData } from '@moment/bridge/client-bridge-defs.ts';
import {
  generateWeatherComboNoGeoDataQuery,
  generateWeatherComboQuery,
} from '@moment/net/weather-fetch-query.ts';
import WeatherSkeleton from '@moment/components/Weather/WeatherSkeleton.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';

type BridgeGeo = {
  latitude: number;
  longitude: number;
};

const WeatherWrapper: FC = () => {
  const [bridgeGeo, setBridgeGeo] = useState<undefined | BridgeGeo | null>(
    undefined
  );
  const bridge = useBridge();
  const { LOCATION_API_BASE_URL } = useContext(CommonPageDataContext);

  function truncateToTwoDecimalPlaces(num: number | string): string | null {
    const match = num?.toString().match(/^-?\d+(?:\.\d{0,2})?/);
    if (!match || !match[0]) return null;
    const parts = match[0].split('.');
    const integerPart = parts?.length >= 0 ? parts[0] : '';
    let decimalPart = parts?.length > 0 ? parts[1] : '';
    decimalPart = (decimalPart || '').padEnd(2, '0');
    return `${integerPart}.${decimalPart}`;
  }

  useEffect(() => {
    try {
      const deviceInfoConst: any = bridge.getData(BridgeData.DEVICE_INFO);
      console.debug(
        `DeviceInfo from bridge: ${JSON.stringify(deviceInfoConst)}`
      );
      if (deviceInfoConst as any) {
        const truncLatitude = truncateToTwoDecimalPlaces(deviceInfoConst.lat);
        const truncLongitude = truncateToTwoDecimalPlaces(deviceInfoConst.lng);
        console.debug(
          `Truncated geolocation: ${truncLatitude}, ${truncLongitude}`
        );
        if (truncLatitude && truncLongitude) {
          setBridgeGeo({
            latitude: parseFloat(truncLatitude),
            longitude: parseFloat(truncLongitude),
          });
        } else {
          console.warn(`Invalid geolocation data in device data.`);
          setBridgeGeo(null);
        }
      } else {
        console.warn(`Geolocation data not found in device data.`);
        setBridgeGeo(null);
      }
    } catch (e) {
      console.error(`Error getting geolocation from bridge: ${e}`);
      setBridgeGeo(null);
    }
  }, []);

  const weatherComboQueryFn = useCallback(() => {
    return bridgeGeo
      ? generateWeatherComboQuery(LOCATION_API_BASE_URL)(
          bridgeGeo.latitude,
          bridgeGeo.longitude
        )
      : null;
  }, [LOCATION_API_BASE_URL, bridgeGeo]);

  const weatherComboNoGeoDataQueryFn = useCallback(() => {
    return generateWeatherComboNoGeoDataQuery(LOCATION_API_BASE_URL)();
  }, [LOCATION_API_BASE_URL]);

  const {
    data: weatherComboData,
    isLoading: isWeatherComboLoading,
    error: weatherComboError,
  } = useQuery<WeatherV4Response>({
    queryKey: ['weather-combo', bridgeGeo?.latitude, bridgeGeo?.longitude],
    queryFn: weatherComboQueryFn,
    enabled: !!bridgeGeo && !!LOCATION_API_BASE_URL,
    retry: false,
  });

  const {
    data: weatherComboNoGeoData,
    isLoading: isWeatherComboNoGeoLoading,
    error: weatherComboNoGeoError,
  } = useQuery<WeatherV4Response>({
    queryKey: ['weather-combo-nogeo'],
    queryFn: weatherComboNoGeoDataQueryFn,
    enabled: bridgeGeo === null && !!LOCATION_API_BASE_URL,
    retry: false,
  });

  const isNight = useMemo(() => {
    const currentHour = new Date().getHours();
    return currentHour >= 18 || currentHour < 6;
  }, []);

  const { location, current, forecast } = useMemo(() => {
    return (
      weatherComboData ||
      weatherComboNoGeoData || { location: null, current: null, forecast: null }
    );
  }, [weatherComboData, weatherComboNoGeoData]);

  if (isWeatherComboLoading || isWeatherComboNoGeoLoading) {
    return <WeatherSkeleton />;
  }

  if (weatherComboError || weatherComboNoGeoError) {
    console.error(
      `Error fetching weather data: ${weatherComboError || weatherComboNoGeoError}`
    );
    return (
      <Weather location={null} current={null} daily={null} night={isNight} />
    );
  }

  return (
    <Weather
      location={location}
      current={current}
      daily={forecast}
      night={isNight}
    />
  );
};

export default WeatherWrapper;
